// The customer-facing contract RENEWAL page (Session AII, "Renewal B"). Reached only by
// Contract.renewalToken, and only once the office has confirmed the lift register (Session AIG).
//
// IT DELIBERATELY REUSES THE QUOTE ACCEPT PAGE'S CHROME rather than inventing a second customer
// look. AcceptQuote.css is a scoped `.cq` design system carrying the light-document treatment the
// Director signed off (Session AHD) and the seven-colour audit that followed (AHT): the dark header
// contrast band, the card and tier styling, the AA-corrected ink variants, the one permitted panel
// tint. Writing a fresh stylesheet here would mean re-deriving all of that and getting some of it
// wrong -- and the two pages are the same customer, days apart, so they should look like one company.
//
// NOTHING IS PRICED IN THE BROWSER. Every figure on this page came from the server: the initial load
// prices the current terms, and each change round-trips to the reprice endpoint. The customer's
// controls are applied optimistically and REVERTED if the reprice fails, so the price on screen and
// the controls above it can never disagree -- the pattern the quote accept page established.
import { NO_SIGNATORY, SignatoryQuestion, signatoryToSend, type SignatoryAnswer } from "../../components/SignatoryQuestion";
import { useCallback, useEffect, useState } from "react";
import type { ReactNode } from "react";
import { useParams } from "react-router-dom";
import { api, ApiError } from "../../lib/api";
import type { PublicRenewal, PublicTierKey } from "../../lib/api";
// Reuses the quote accept page's OWN footer (company legal copy, WhatsApp link and the masked
// skyline watermark) rather than a second implementation: the Director's "exactly the same as the
// new customer" instruction extends to how the page looks, and importing the real component makes
// that true by construction instead of by two files happening to agree.
// Session AJN: the same reuse-not-fork rule now covers the Terms/Maintenance modals and the
// Share/Print section -- all three are exported from AcceptQuote.tsx for exactly this purpose.
// Session AJW: TIER_CARD_COPY joins them -- the real per-lift tier card content (blurb + the
// full bulleted includes list with the actual figures), replacing this page's own thinner
// one-line-per-tier summary that could drift from the locked tier figures. Renewals are always
// lift-covered (buildRenewalContext refuses `mixed_contract` at load, server/src/modules/
// contracts/renewalAccept.ts:362), so this is the per-lift copy -- never AcceptQuote.tsx's
// separate lift-less variant, which a lift-covered renewal can never reach.
// Session APD: standardTermsApplyTo and AGREEMENT_TERMS_NOTE join them, so this page decides whether to offer
// the standard Terms and Conditions exactly as the quote page does, and says the same thing when it does not.
import {
  Footer,
  TermsModal,
  MaintenanceModal,
  ScheduleOfFeesModal,
  ShareprintSection,
  TIER_CARD_COPY,
  standardTermsApplyTo,
  AGREEMENT_TERMS_NOTE,
} from "./AcceptQuote";
import "./AcceptQuote.css";
import "./RenewalAccept.css";

const PHONE = "0203 576 4760";

function gbp(n: number): string {
  return `£${Math.round(n).toLocaleString("en-GB")}`;
}

function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="cq">
      <div className="cust-hdr">
        <div className="brand">
          <img className="brand-logo" src="/brand/ele-logo.png" alt="East London Elevators" />
          <small>Service Agreement Renewal</small>
        </div>
        <div className="hdr-contact">
          <b>{PHONE}</b>
          <br />
          info@eastlondonelevators.co.uk
          <br />
          <a href="https://eastlondonelevators.co.uk" target="_blank" rel="noopener noreferrer">
            eastlondonelevators.co.uk
          </a>
        </div>
      </div>
      <div className="wrap ele-fade-in">{children}</div>
    </div>
  );
}

export function RenewalAccept() {
  const { token = "" } = useParams();
  const [renewal, setRenewal] = useState<PublicRenewal | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<"not_found" | "not_ready" | "already_renewed" | "error" | null>(null);
  // Session APB: the server's own sentence for an agreement that has already renewed, shown as given.
  const [loadMessage, setLoadMessage] = useState<string | null>(null);

  // The customer's two choices. Held separately from `renewal` so a control can move immediately
  // while the server price catches up, and be put back if the reprice fails.
  const [tier, setTier] = useState<PublicTierKey | null>(null);
  const [frequency, setFrequency] = useState<string>("");
  const [repricing, setRepricing] = useState(false);
  const [repriceError, setRepriceError] = useState<string | null>(null);

  const [submitting, setSubmitting] = useState(false);
  // Session AUJ: "Is someone else signing?" Default "No, I'll sign", which sends nothing.
  const [signatoryAnswer, setSignatoryAnswer] = useState<SignatoryAnswer>(NO_SIGNATORY);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Session AJN, matching AcceptQuote.tsx: which info modal is open (Terms, Maintenance Schedule
  // or Schedule of Fees, Session AKR), or none.
  const [modal, setModal] = useState<"terms" | "maintenance" | "fees" | null>(null);
  // Session AIK: every renewal now ends in a signature, so there is one finished state and no
  // "recorded, nothing more to do" branch to distinguish.
  // A finished state carries an optional QUOTATION to go and read: a tier change is handed the full
  // quotation document rather than signed here, so the customer's next step is a link, not an inbox.
  const [done, setDone] = useState<{ message: string; quote?: { url: string; ref: string } | null } | null>(null);

  useEffect(() => {
    let live = true;
    setLoading(true);
    api.publicRenewal
      .get(token)
      .then((r) => {
        if (!live) return;
        setRenewal(r);
        setTier(r.selected.tier);
        setFrequency(r.selected.frequency);
        if (r.status !== "open") {
          // A raised quotation is NOT an agreement awaiting signature, and saying so would send the
          // customer to an inbox for an email nobody has sent. It is a document waiting to be read,
          // and the link back to it is the whole point of showing this state at all.
          if (r.status === "quote_issued" && r.issuedQuote) {
            setDone({
              message: "We have prepared your new quotation. Please read it through and accept it there to complete your renewal.",
              quote: { url: r.issuedQuote.url, ref: r.issuedQuote.quoteRef },
            });
            return;
          }
          setDone({
            message:
              r.status === "accepted"
                ? "Thank you. Your renewal is complete and your signed agreement is on file."
                : "Your renewed service agreement needs signing. Please check your email, or call the office if it has not arrived.",
          });
        }
      })
      .catch((err) => {
        if (!live) return;
        if (err instanceof ApiError && err.status === 404) setLoadError("not_found");
        else if (err instanceof ApiError && err.status === 409 && err.body?.error === "already_renewed") {
          // Session APB, Director decision 2: this cycle's price has already moved, so the agreement has
          // renewed and the link is refused. Its own state, because the generic one below would tell the
          // customer "we are finishing a few checks on your lifts", which would be false.
          setLoadError("already_renewed");
          setLoadMessage(typeof err.body.message === "string" ? err.body.message : null);
        } else if (err instanceof ApiError && err.status === 409) setLoadError("not_ready");
        else setLoadError("error");
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [token]);

  // Session APB review: a page opened BEFORE the agreement renewed through the ledger, used after it. The
  // server refuses the reprice and the accept alike with the same "already renewed" answer the load gives,
  // so the page moves to that state rather than leaving prices and a live Confirm button on screen.
  const renewedSince = useCallback((err: unknown): boolean => {
    if (!(err instanceof ApiError && err.status === 409 && err.body?.error === "already_renewed")) return false;
    setLoadMessage(typeof err.body.message === "string" ? err.body.message : null);
    setLoadError("already_renewed");
    return true;
  }, []);

  // Reprice from the SERVER for the given choices, restoring the caller's previous control state if
  // it fails, so the controls always describe the price on screen.
  const doReprice = useCallback(
    async (nextTier: PublicTierKey, nextFrequency: string, revert: () => void) => {
      setRepriceError(null);
      setRepricing(true);
      try {
        const repriced = await api.publicRenewal.preview(token, nextTier, nextFrequency);
        setRenewal(repriced);
      } catch (err) {
        revert();
        if (renewedSince(err)) return;
        setRepriceError(
          err instanceof ApiError && err.status === 422
            ? `That option is not available for these lifts. Please choose another, or call the office on ${PHONE}.`
            : `We could not update the price just now. Please try again, or call the office on ${PHONE}.`,
        );
      } finally {
        setRepricing(false);
      }
    },
    [token, renewedSince],
  );

  const chooseTier = useCallback(
    (next: PublicTierKey) => {
      if (!tier || next === tier) return;
      const prev = tier;
      setTier(next);
      void doReprice(next, frequency, () => setTier(prev));
    },
    [tier, frequency, doReprice],
  );

  const chooseFrequency = useCallback(
    (next: string) => {
      if (!tier || next === frequency) return;
      const prev = frequency;
      setFrequency(next);
      void doReprice(tier, next, () => setFrequency(prev));
    },
    [tier, frequency, doReprice],
  );

  const onAccept = useCallback(async () => {
    if (!tier || submitting) return;
    // Session AUJ: asked only where this page raises the agreement (the same cover level); a cover change is
    // handed a quotation whose own page asks again. Never carried from an earlier renewal.
    const signing = renewal?.selected.basis === "reprice" ? {} : signatoryToSend(signatoryAnswer);
    if (signing.error) {
      setSubmitError(signing.error);
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = signing.signatory
        ? await api.publicRenewal.accept(token, tier, frequency, signing.signatory)
        : await api.publicRenewal.accept(token, tier, frequency);
      setDone({
        message: res.message,
        quote: res.status === "quote_issued" && res.quoteUrl ? { url: res.quoteUrl, ref: res.quoteRef ?? "" } : null,
      });
    } catch (err) {
      if (renewedSince(err)) return;
      if (err instanceof ApiError && err.status === 409) {
        setSubmitError(
          typeof err.code === "string" && err.code.includes(" ")
            ? err.code
            : `This renewal has already been completed. Please call the office on ${PHONE}.`,
        );
      } else if (err instanceof ApiError && err.status === 429) {
        setSubmitError(`Please wait a moment and try again, or call the office on ${PHONE}.`);
      } else if (err instanceof ApiError && typeof err.code === "string" && err.code.includes(" ")) {
        setSubmitError(err.code);
      } else {
        setSubmitError(`Something went wrong. Please try again, or call the office on ${PHONE}.`);
      }
    } finally {
      setSubmitting(false);
    }
  }, [tier, frequency, token, submitting, renewedSince, renewal, signatoryAnswer]);

  if (loading) {
    return (
      <Shell>
        <p className="intro">Loading your renewal...</p>
      </Shell>
    );
  }

  // Session APB: the one refusal the customer is told specifically (the Director's instruction): their
  // agreement has already renewed for the coming year. The server supplies the sentence; the fallback
  // says the same thing, for a response that somehow arrives without it.
  if (loadError === "already_renewed") {
    return (
      <Shell>
        <div className="sec">
          <h1 className="sec-title">Your agreement has already renewed</h1>
          <p className="intro">
            {loadMessage ??
              `Your maintenance agreement has already renewed for the coming year, so there is nothing to renew here. If this looks wrong, please call the office on ${PHONE}.`}
          </p>
        </div>
      </Shell>
    );
  }

  // ONE generic panel for every other reason the link does not resolve. The server never tells this page
  // which reason it was, so the page cannot leak one either.
  if (loadError) {
    return (
      <Shell>
        <div className="sec">
          <h1 className="sec-title">
            {loadError === "not_found" ? "We could not find this renewal" : "This renewal is not ready just yet"}
          </h1>
          <p className="intro">
            {loadError === "not_found"
              ? "This link may have expired or already been used."
              : "We are finishing a few checks on your lifts before you can complete this online."}{" "}
            Please call the office on <b>{PHONE}</b> and we will sort it out with you.
          </p>
        </div>
      </Shell>
    );
  }

  if (!renewal || !tier) return null;

  // Session AJN: what to hand ShareprintSection's Share button, which needs an identifying string
  // for its native-share title -- it has no separate "the current page URL" concept to configure,
  // it always shares window.location.href. A renewal carries no reference of its own (the public
  // DTO deliberately exposes no contract id -- see PublicRenewalView's header), and no Quote exists
  // yet on the review screen either way: a same-tier renewal never mints one (AIK decision 1), and a
  // tier change only mints one once accepted. Once accepted with a tier change, the real quote ref
  // AJL raised is known and is truer, so it wins; every other state falls back to the site name, the
  // one identifier this page shows that a customer would actually recognise on the phone.
  const shareRef = done?.quote?.ref || renewal.siteName;

  if (done) {
    // WHAT THEY AGREED, not what the contract currently says. On a tier change the contract stays on
    // its old tier until the signature lands, so reading `renewal.selected` back here would show a
    // customer who has just moved to Complete that they had bought Care. The server returns the
    // accepted terms once they exist; `chosen` covers the same visit, before any reload.
    const agreed = renewal.accepted ?? {
      tierLabel: renewal.tierOptions.find((t) => t.value === tier)?.label ?? tier,
      // The CONTRACT'S cadence, never the one in the dropdown. A requested change is a separate
      // matter the office confirms (Session AIK), so listing it here as "Visits" would tell the
      // customer they had bought a schedule they have not.
      frequencyLabel: renewal.current.frequencyLabel,
      annual: renewal.selected.annual,
    };
    // A cadence request survives a reload through requestedFrequencyLabel; before any reload the
    // dropdown itself is the record of what they asked for.
    const requestedLabel =
      renewal.requestedFrequencyLabel ??
      (frequency !== renewal.current.frequency
        ? (renewal.frequencyOptions.find((f) => f.value === frequency)?.label ?? null)
        : null);
    return (
      <Shell>
        <div className="sec">
          <h1 className="sec-title">Almost there</h1>
          <p className="intro">{done.message}</p>
          {/* THE HANDOFF. A tier change is quoted, not signed here, so the customer's next step is to
              go and read the quotation. It is the primary action on this panel because without it the
              renewal simply stops: there is no email to wait for and nothing else to press. */}
          {done.quote ? (
            <p className="quote-handoff">
              <a className="confirm" href={done.quote.url}>
                Read and accept your quotation
              </a>
              {done.quote.ref ? <span className="quote-ref">Reference {done.quote.ref}</span> : null}
            </p>
          ) : null}
          {/* .sel-box is the dark contrast band .sel-k/.sel-v are authored for (AcceptQuote.css);
              without it these render as light-on-white text on this white card, invisible. */}
          <div className="summary">
            <div className="sel-box">
              <div className="sel-line">
                <span className="sel-k">Site</span>
                <span className="sel-v">{renewal.siteName}</span>
              </div>
              <div className="sel-line">
                <span className="sel-k">Cover level</span>
                <span className="sel-v">{agreed.tierLabel}</span>
              </div>
              <div className="sel-line">
                <span className="sel-k">Visits</span>
                <span className="sel-v">{agreed.frequencyLabel}</span>
              </div>
              <div className="sel-line">
                <span className="sel-k">Annual price</span>
                <span className="sel-v">{gbp(agreed.annual)} plus VAT</span>
              </div>
              {requestedLabel ? (
                <div className="sel-line">
                  <span className="sel-k">Frequency change</span>
                  <span className="sel-v">{requestedLabel}, with the office</span>
                </div>
              ) : null}
            </div>
          </div>
        </div>
        {/* Session AJN: rendered unconditionally on the confirmed panel too, matching AHS's
            placement rule -- Share/Print is useful whether the customer is reviewing, mid-change
            or already done. documentLabel (Session AJV): this is a renewal, not a fresh quote, so
            the native share title says so. */}
        <ShareprintSection quoteRef={shareRef} documentLabel="Service Agreement Renewal" />
        <Footer />
      </Shell>
    );
  }

  const delta = renewal.delta;
  // Used in the frequency notice below; falls back to the raw value in the vanishingly unlikely case
  // the server ever returns a chosen frequency not on its own offered list (defence in depth -- the
  // server already refuses that with isRenewalFrequencyFor before this page ever sees it).
  const chosenFrequencyLabel = renewal.frequencyOptions.find((f) => f.value === frequency)?.label ?? frequency;
  // Every tier card reads the same live engine estimate as the frequency notice and the summary box
  // whenever one is showing (Session AJO's basis, extended to the cards): all three move together, so
  // this one flag is enough to tell every card to say so too, rather than each guessing from its own
  // price alone.
  const cardsAreEstimated = renewal.selected.basis === "estimate";

  return (
    <Shell>
      {/* AcceptQuote.tsx's own page title sits directly on the canvas at 26px (.intro h1 / .lead),
          not boxed into a .sec card at .sec-title's 17px: a genuine header-chrome mismatch found in
          the full diff, fixed by reusing the same wrapper AcceptQuote already uses rather than a
          smaller lookalike heading. */}
      <div className="intro">
        <h1>Renew your service agreement</h1>
        <p className="lead">
          Your agreement for <b>{renewal.siteName}</b>
          {renewal.renewalDateDisplay ? <> is due for renewal on <b>{renewal.renewalDateDisplay}</b></> : null}. Your lifts stay
          exactly as they are.{" "}
          {renewal.tierChangeAvailable
            ? "Check the price for the year ahead below, and change your cover level if you would like to."
            : "Check the price for the year ahead below."}{" "}
          When you confirm, we will email you your renewed agreement to sign.
        </p>
      </div>

      {/* THE REGISTER, READ-ONLY. Presented as a statement of fact, with no controls of any kind, because
          the customer genuinely cannot change it here. */}
      <div className="sec">
        <h2 className="sec-title">The lifts we maintain for you</h2>
        <p className="sec-note">
          {renewal.siteAddress ? `${renewal.siteAddress}. ` : ""}
          To add or remove a lift, please call the office on {PHONE}.
        </p>
        <div className="rnw-register">
          {renewal.lifts.map((l) => (
            <div className="rnw-lift" key={l.eleNumber ?? `${l.liftType}-${l.stops}`}>
              <span className="rnw-lift-ref">{l.eleNumber ?? "Lift"}</span>
              <span className="rnw-lift-spec">
                {[l.liftType, l.stops ? `${l.stops} floors` : null].filter(Boolean).join(" · ")}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* THE CADENCE IS STILL A REQUEST, AND THE PAGE SAYS SO BEFORE THEY PICK ONE (Session AIK). More
          or fewer visits a year is a commitment to staff a different pattern, so the office confirms
          it; the agreement raised here is always signed at the schedule they are currently on, whatever
          is selected below. THE PRICE SHOWN FOR A DIFFERENT FREQUENCY IS NOW A LIVE ESTIMATE, NOT A
          FROZEN FIGURE (Session AJO). The page used to show the identical number whichever frequency
          was picked, which a customer comparing Monthly against Quarterly could reasonably read as
          "Monthly is free" -- misleading, not merely unhelpful. It now shows what the engine genuinely
          computes for that cadence, labelled plainly as an estimate the office will confirm rather than
          a firm price. */}
      <div className="sec">
        <h2 className="sec-title">How often we visit</h2>
        <p className="sec-note">
          You are on <b>{renewal.current.frequencyLabel}</b> visits. Your renewal continues on that schedule. If you would like
          to change it, tell us here and the office will confirm the change, and anything it affects, with you separately.
        </p>
        <div className="rnw-options" role="group" aria-label="Visit frequency">
          {renewal.frequencyOptions.map((o) => (
            <button
              key={o.value}
              type="button"
              className={`rnw-option${frequency === o.value ? " rnw-option-on" : ""}`}
              aria-pressed={frequency === o.value}
              onClick={() => chooseFrequency(o.value)}
              disabled={repricing || submitting}
            >
              {o.label}
            </button>
          ))}
        </div>
        {renewal.frequencyChangeRequested ? (
          <p className="notice" role="status">
            We will pass your request to move to {chosenFrequencyLabel} to the office, and they will confirm the change, and
            anything it affects, with you separately.{" "}
            {renewal.selected.basis === "estimate" ? (
              <>
                At {String(chosenFrequencyLabel).toLowerCase()} visits, this would cost approximately {gbp(renewal.selected.annual)} a
                year. We will confirm the exact figure with you if you go ahead with this change.
              </>
            ) : (
              <>The price below is still for your current {renewal.current.frequencyLabel.toLowerCase()} visits.</>
            )}
          </p>
        ) : null}
      </div>

      {/* A TIER WITH NO PRICE IS NOT OFFERED (Session AIK). It happens when this contract's price was
          negotiated, or when today's calculator cannot reproduce what it already charges, so quoting a
          different cover level would put a number in front of the customer that nobody agreed. The
          card still shows what the tier IS, and points at the office, rather than vanishing and
          leaving them to wonder what happened to it. */}
      <div className="sec">
        <h2 className="sec-title">Your cover level</h2>
        {!renewal.tierChangeAvailable ? (
          <p className="sec-note">
            Your renewal continues on your current cover. To move to a different level, please call the office on {PHONE} and
            we will talk the options through with you.
          </p>
        ) : null}
        <div className="tier-grid">
          {renewal.tierOptions.map((o) => {
            const price = o.annual;
            const isCurrent = o.value === renewal.current.tier;
            const isChosen = o.value === tier;
            const offered = price != null;
            // Session AJN: whether any of the card's selection paths (whole-card click, radio,
            // CTA button) may act. AcceptQuote.tsx's TierCard has no equivalent guard -- every tier
            // is always choosable there -- because it has no "not offered" state; this page does.
            const cardClickable = offered && !repricing && !submitting;
            // Same tier-identity class AcceptQuote.tsx's TierCard renders (`ctier ${tierKey}`), so
            // the name colour, the CTA border and the selected fill/glow all come from the SAME
            // per-tier rules in AcceptQuote.css (green/purple/blue) rather than a second, renewal-
            // only "chosen" treatment that never carried tier identity at all.
            return (
              <div
                className={`ctier ${o.value}${isChosen ? " sel" : ""}`}
                data-key={o.value}
                key={o.value}
                // Session AJN: the whole card selects, matching AcceptQuote.tsx's TierCard onSelect
                // wiring exactly.
                onClick={() => {
                  if (cardClickable) chooseTier(o.value);
                }}
              >
                {/* The label+radio pair is the keyboard/assistive-tech path, AcceptQuote.tsx's own
                    `.ctier-head` structure. Its onClick stops the click bubbling to the card root
                    above: chooseTier reprices from the server, so (unlike AcceptQuote's onSelect,
                    plain local state with no side effect) letting both the root's onClick and the
                    radio's onChange fire from one click would send the reprice request twice. */}
                <label className="ctier-head" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="radio"
                    name="tier"
                    value={o.value}
                    checked={isChosen}
                    onChange={() => chooseTier(o.value)}
                    disabled={!cardClickable}
                    aria-label={o.label}
                  />
                  <span className="ctier-name">{o.label}</span>
                  {isCurrent ? <span className="rnw-current-badge">Your current cover</span> : null}
                </label>
                <div className="ctier-price">{offered ? gbp(price) : "Call us"}</div>
                {/* The estimate qualifier follows the SAME renewal.selected.basis flag the frequency
                    notice and the summary note already read, so a card can never claim a firm price
                    where the text around it calls the identical number an estimate. */}
                <div className="ctier-period">
                  {offered ? `a year plus VAT${cardsAreEstimated ? " (estimated)" : ""}` : `on ${PHONE}`}
                </div>
                {/* Session AJW: the same blurb + tick-list markup AcceptQuote.tsx's TierCard
                    renders (.ctier-desc / .ctier-incl, styled by the shared AcceptQuote.css),
                    reading from the SAME exported TIER_CARD_COPY constant rather than a second,
                    thinner copy of it. */}
                <p className="ctier-desc">{TIER_CARD_COPY[o.value].blurb}</p>
                <ul className="ctier-incl">
                  {TIER_CARD_COPY[o.value].includes.map((line) => (
                    <li key={line}>
                      <span className="tick" aria-hidden>
                        {"✓"}
                      </span>
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  className="ctier-cta"
                  aria-pressed={isChosen}
                  onClick={(e) => {
                    e.stopPropagation(); // the card root also selects; one call is enough
                    chooseTier(o.value);
                  }}
                  disabled={!cardClickable}
                >
                  {isChosen ? "Selected" : offered ? `Choose ${o.label}` : "Not available online"}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* THE PRICE AND THE CHANGE, BEFORE THEY ACCEPT. The delta is the whole point of this panel: a
          renewal that quietly cost more than last year would be the surprise this page exists to avoid. */}
      <div className="sec">
        <h2 className="sec-title">What this comes to</h2>
        {/* .sel-box: see the note on the confirmation panel above -- same fix, same reason. */}
        <div className="summary" aria-live="polite">
          <div className="sel-box">
            <div className="sel-line">
              <span className="sel-k">You pay now</span>
              <span className="sel-v">{gbp(renewal.current.annual)} a year</span>
            </div>
            <div className="sel-line">
              <span className="sel-k">Your renewal</span>
              <span className="sel-v">
                <b>{gbp(renewal.selected.annual)} a year</b> plus VAT
              </span>
            </div>
            <div className="sel-line">
              <span className="sel-k">Change</span>
              <span className={`sel-v rnw-delta${delta > 0 ? " rnw-delta-up" : delta < 0 ? " rnw-delta-down" : ""}`}>
                {delta === 0 ? "No change" : `${delta > 0 ? "+" : "-"}${gbp(Math.abs(delta))} a year`}
              </span>
            </div>
          </div>
        </div>
        {/* WHERE THE NUMBER CAME FROM, in the customer's own terms. An unchanged cover level is the
            annual review their agreement already provides for; a change of cover is a fresh quote.
            Those are different promises and the page should not leave them to guess which. */}
        <p className="sec-note">
          {renewal.selected.basis === "escalation"
            ? delta === 0
              ? "Your price is unchanged for the year ahead."
              : "This is your current annual charge with the yearly review set out in your agreement applied to it. Your lifts and your visits are unchanged."
            : renewal.selected.basis === "override"
              ? delta === 0
                ? "Your price is unchanged for the year ahead."
                : "This is the annual charge agreed for your renewal. Your lifts and your visits are unchanged."
              : renewal.selected.basis === "estimate"
                ? "This is an estimate for the visit frequency you have chosen, not a confirmed price. Your renewal continues on your current visits unless the office confirms the change."
                : "You have chosen a different cover level, so this is a fresh price for that cover at today's rates."}
        </p>
        {repricing ? <p className="sec-note">Updating your price...</p> : null}
        {repriceError ? (
          <p className="cq-error" role="alert">
            {repriceError}
          </p>
        ) : null}
      </div>

      <div className="sec">
        {/* EVERY RENEWAL IS SIGNED (Session AIK), so this is unconditional. The old page said this only
            for a cover change and let the rest complete on a click, which is what the Director's
            ruling reverses: the renewed term is a fresh agreement whatever the cover level, and this
            is the point at which a legacy contract gets a signature on file at last. */}
        <p className="notice">
          When you confirm, we will email you your renewed service agreement to sign. Nothing changes until you have signed
          it, and your current agreement continues in the meantime.
        </p>
        {renewal.selected.basis !== "reprice" ? (
          <SignatoryQuestion customerName={null} value={signatoryAnswer} onChange={setSignatoryAnswer} disabled={submitting} />
        ) : null}
        {submitError ? (
          <p className="cq-error" role="alert">
            {submitError}
          </p>
        ) : null}
        <button type="button" className="confirm" onClick={onAccept} disabled={submitting || repricing}>
          {submitting ? "Please wait..." : `Confirm and send my agreement to sign`}
        </button>
        {/* Session AJN: same position, same buttons, same modals as AcceptQuote.tsx -- next to the
            confirm button, opening the imported TermsModal / MaintenanceModal directly rather than
            a forked copy. */}
        <div className="cq-modal-buttons">
          {/* Session APD: the standard terms only for a customer who signs the Standard ESA
              (standardTermsApplyTo, AcceptQuote.tsx). An intermediary renews on the Intermediary variant. */}
          {standardTermsApplyTo(renewal) ? (
            <button type="button" className="cq-info-btn" onClick={() => setModal("terms")}>
              Terms and Conditions
            </button>
          ) : null}
          <button type="button" className="cq-info-btn" onClick={() => setModal("maintenance")}>
            Maintenance Schedule
          </button>
          {/* Session AKR: same modal, same place as AcceptQuote.tsx -- converts Session AKD's
              plain PDF link into an in-page modal matching Terms and Conditions / Maintenance
              Schedule, per the Director-confirmed gap. No shared wrapper exists for this button
              row (each page owns its own copy), so it is added at both call sites explicitly. */}
          <button type="button" className="cq-info-btn" onClick={() => setModal("fees")}>
            Schedule of Fees
          </button>
        </div>
        {/* Session APD: where an intermediary's terms are, in place of the button they are not offered. */}
        {standardTermsApplyTo(renewal) ? null : <p className="cq-terms-note">{AGREEMENT_TERMS_NOTE}</p>}
        <p className="fineprint">
          Prices exclude VAT. Your agreement continues on the same terms unless you change them above. If anything here does
          not look right, call the office on {PHONE} before confirming.
        </p>
      </div>
      {/* Session AJN: unconditional, matching AHS's placement rule -- page load and mid-review here,
          the confirmed panel above. documentLabel (Session AJV): see the confirmed-panel render
          site above for why this reads "Service Agreement Renewal" rather than the default. */}
      <ShareprintSection quoteRef={shareRef} documentLabel="Service Agreement Renewal" />
      <Footer />

      {modal === "terms" && standardTermsApplyTo(renewal) && <TermsModal onClose={() => setModal(null)} />}
      {modal === "maintenance" && <MaintenanceModal onClose={() => setModal(null)} />}
      {modal === "fees" && <ScheduleOfFeesModal onClose={() => setModal(null)} />}
    </Shell>
  );
}
